import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field, FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { DatePicker } from '@/components/DatePicker'

// Each row is an active Employment that has no active ProjectAssignment as of `as_of`.
type BenchRow = {
  id: number  // Employment id
  employee_number: string
  full_name: string
  employment_type: string
  department_name: string | null
  manager_name: string | null
  last_project_end_date: string | null
  days_on_bench: number | null
}

type BenchResponse = {
  as_of: string
  bench_count: number
  total_billable_active: number
  utilization_pct: number
  bench_pct: number
  employees: BenchRow[]
}

export default function HRBench() {
  const today = new Date().toISOString().slice(0, 10)
  const [asOf, setAsOf] = useState(today)
  const navigate = useNavigate()

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['hr', 'bench', asOf],
    queryFn: async () =>
      (await api.get<BenchResponse>(`/hr/utilization/bench/?as_of=${asOf}`)).data,
  })

  const rows = data?.employees || []

  return (
    <AdminLayout>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Bench & Utilization</h2>
        <p className="text-sm text-gray-500 mt-1">
          Active employments in billable departments with no active project assignment as of the selected date.
          Non-billable departments (HR, Finance, etc.) are absorbed and not counted as on bench.
        </p>
      </div>

      <div className="flex items-end gap-4 mb-6">
        <Field>
          <FieldLabel htmlFor="bench-asof">As of</FieldLabel>
          <DatePicker
            id="bench-asof"
            value={asOf}
            onChange={(v) => setAsOf(v || today)}
            className="w-44"
          />
        </Field>
        {isFetching && <span className="text-xs text-muted-foreground pb-2">Loading…</span>}
      </div>

      {isLoading || !data ? (
        <p className="text-gray-500">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <KPI label="On bench" value={data.bench_count} sub={`${data.bench_pct}% of billable`} />
            <KPI label="Billable active" value={data.total_billable_active} />
            <KPI
              label="Utilization"
              value={`${data.utilization_pct}%`}
              color={data.utilization_pct >= 80 ? 'text-green-700' : data.utilization_pct >= 60 ? 'text-amber-700' : 'text-red-700'}
            />
          </div>

          {rows.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
              No one on the bench as of {data.as_of}. 100% utilization — nice.
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-4 py-2 font-semibold text-gray-600">#</th>
                    <th className="px-4 py-2 font-semibold text-gray-600">Name</th>
                    <th className="px-4 py-2 font-semibold text-gray-600">Type</th>
                    <th className="px-4 py-2 font-semibold text-gray-600">Department</th>
                    <th className="px-4 py-2 font-semibold text-gray-600">Manager</th>
                    <th className="px-4 py-2 font-semibold text-gray-600">Last project end</th>
                    <th className="px-4 py-2 font-semibold text-gray-600 text-right">Days on bench</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const days = r.days_on_bench ?? null
                    const dayColor = days === null
                      ? 'text-gray-400'
                      : days > 60 ? 'text-red-700 font-semibold'
                      : days > 30 ? 'text-amber-700'
                      : 'text-gray-700'
                    return (
                      <tr
                        key={r.id}
                        className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                        onClick={() => navigate(`/admin/hr/employments/${r.id}`)}
                      >
                        <td className="px-4 py-2 font-mono text-xs">{r.employee_number}</td>
                        <td className="px-4 py-2">
                          <div className="font-medium">{r.full_name}</div>
                        </td>
                        <td className="px-4 py-2 text-xs">{r.employment_type}</td>
                        <td className="px-4 py-2 text-xs">{r.department_name || '—'}</td>
                        <td className="px-4 py-2 text-xs">{r.manager_name || '—'}</td>
                        <td className="px-4 py-2 text-xs">{r.last_project_end_date || <span className="text-gray-400">never assigned</span>}</td>
                        <td className={`px-4 py-2 text-right ${dayColor}`}>
                          {days === null ? '—' : days}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  )
}

function KPI({ label, value, sub, color }: { label: string; value: number | string; sub?: string; color?: string }) {
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${color || 'text-gray-800'}`}>{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  )
}
